import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Key,
  FileText,
  Users,
  Megaphone,
  Send,
  Building2,
  DollarSign,
  MessageSquare,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { useEnabledModules } from '@/hooks/useModules';

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
    isActive ? 'bg-white/15 text-white' : 'text-white/55 hover:bg-white/10 hover:text-white',
  );

const defaultNavItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { to: '/credentials', icon: Key, label: 'Credenciais', exact: false },
  { to: '/templates', icon: FileText, label: 'Templates', exact: false },
  { to: '/contacts', icon: Users, label: 'Contatos', exact: false },
  { to: '/dispatches', icon: Send, label: 'Disparos', exact: false },
];

export function Sidebar() {
  const isSuperadmin = useAuthStore((s) => s.user?.isSuperadmin ?? false);
  const { data: modules = [] } = useEnabledModules();

  const hasCampanhas = modules.includes('campanhas');
  const hasUplog = modules.includes('financeiro');
  const hasRcs = modules.includes('rcs');

  return (
    <aside className="flex h-full w-60 flex-col" style={{ backgroundColor: '#1a0a38' }}>
      <div className="flex h-14 items-center gap-2 border-b border-white/10 px-4">
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary">
          <Send className="h-3.5 w-3.5 text-white" />
        </div>
        <span className="text-[15px] font-semibold text-white">Hermes</span>
      </div>

      <nav className="flex-1 space-y-0.5 p-3">
        {defaultNavItems.map(({ to, icon: Icon, label, exact }) => (
          <NavLink key={to} to={to} end={exact} className={navLinkClass}>
            <Icon className="h-4 w-4 shrink-0" />
            {label}
          </NavLink>
        ))}

        {hasCampanhas && (
          <NavLink to="/campaigns" className={navLinkClass}>
            <Megaphone className="h-4 w-4 shrink-0" />
            Campanhas
          </NavLink>
        )}

        {hasUplog && (
          <>
            <div className="my-2 border-t border-white/10" />
            <NavLink to="/uplog" className={navLinkClass}>
              <DollarSign className="h-4 w-4 shrink-0" />
              UpLog
            </NavLink>
          </>
        )}

        {hasRcs && (
          <>
            <div className="my-2 border-t border-white/10" />
            <NavLink to="/rcs" className={navLinkClass}>
              <MessageSquare className="h-4 w-4 shrink-0" />
              RCS
            </NavLink>
          </>
        )}

        {isSuperadmin && (
          <>
            <div className="my-2 border-t border-white/10" />
            <NavLink to="/admin" className={navLinkClass}>
              <Building2 className="h-4 w-4 shrink-0" />
              Clientes
            </NavLink>
          </>
        )}
      </nav>

      <div className="border-t border-white/10 p-4">
        <p className="text-[10px] leading-relaxed text-white/25 text-center">
          Feito com muito amor e café por{' '}
          <a
            href="https://marcelo.dev.br"
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/40 hover:text-white/70 transition-colors underline-offset-2 hover:underline"
          >
            marcelo.dev.br
          </a>
        </p>
      </div>
    </aside>
  );
}
